import * as Yup from 'yup';
import {CurrencyEnum, LocationType as LocationTypeEnum} from 'src/enums';

export interface LocationType extends Record<string, unknown> {
    name: string;
    region: string;
    country: string;
    city: string;
    zipCode: string;
    street: string;
    houseNumber: string;
    parkingLots: number;
    availableAmperes: number;
    locationType?: LocationTypeEnum;
    realEstateId?: number | null;
    defaultPrice: string | number;
    currency: CurrencyEnum;
    // Guest tariff fields (optional)
    guestPricePerKwh?: string | number;
    guestHoldAmount?: string | number;
}

export const locationSchema = Yup.object().shape({
    name: Yup.string().required('schemaRequiredLocationName').max(255),
    country: Yup.string().required('schemaRequiredCountry').max(255),
    region: Yup.string().required('schemaRequiredRegion'),
    street: Yup.string().required('schemaRequiredStreet').max(255),
    zipCode: Yup.string().required('schemaRequiredZip').max(255),
    houseNumber: Yup.string().required('schemaRequiredBuildingNumber').max(255),
    city: Yup.string().required('schemaRequiredCity').max(255),
    // Zero is refused, and the server refuses it too. A location priced at nothing cannot have its
    // sessions costed, closed or invoiced at all - every meter value is rolled back - so the form has
    // to say so under the field rather than let the save come back as a 400.
    defaultPrice: Yup.string()
        .required('schemaRequiredDefaultPrice')
        .test('is-valid-number', 'schemaInvalidNumber', (value) => {
            if (!value || value === '') return false;
            return !isNaN(parseFloat(value));
        })
        .test('is-positive', 'schemaPriceMustBePositive', (value) => {
            if (!value || value === '') return true;
            const num = parseFloat(value);
            return isNaN(num) || num > 0;
        })
        .test('max-decimals', 'schemaMaxDecimals', (value) => {
            if (!value || value === '') return true;
            const decimalPart = String(value).split('.')[1];
            return !decimalPart || decimalPart.length <= 4;
        }),
    currency: Yup.string().required('schemaRequiredCurrency'),
    // The guest tariff is all or nothing: the wrapper sends it as soon as either field is filled, and
    // the server stores the pair as one tariff, so half of it is not a thing that can be saved.
    // Checked with a sibling-reading test rather than two .when() rules, which Yup would reject as a
    // cyclic dependency.
    guestPricePerKwh: Yup.string()
        .nullable()
        .test('is-valid-number', 'schemaInvalidNumber', (value) => {
            if (!value || value === '') return true;
            return !isNaN(parseFloat(value));
        })
        .test('is-positive', 'schemaPriceMustBePositive', (value) => {
            if (!value || value === '') return true;
            const num = parseFloat(value);
            return isNaN(num) || num > 0;
        })
        .test('max-decimals', 'schemaMaxDecimals', (value) => {
            if (!value || value === '') return true;
            const decimalPart = String(value).split('.')[1];
            return !decimalPart || decimalPart.length <= 4;
        })
        .test('guest-tariff-complete', 'schemaGuestTariffIncomplete', function (value) {
            return Boolean(value) === Boolean(this.parent.guestHoldAmount);
        }),
    guestHoldAmount: Yup.string()
        .nullable()
        .test('is-valid-number', 'schemaInvalidNumber', (value) => {
            if (!value || value === '') return true;
            return !isNaN(parseFloat(value));
        })
        .test('is-positive', 'schemaPriceMustBePositive', (value) => {
            if (!value || value === '') return true;
            const num = parseFloat(value);
            return isNaN(num) || num > 0;
        })
        .test('max-decimals', 'schemaMaxDecimals', (value) => {
            if (!value || value === '') return true;
            const decimalPart = String(value).split('.')[1];
            return !decimalPart || decimalPart.length <= 4;
        })
        .test('guest-tariff-complete', 'schemaGuestTariffIncomplete', function (value) {
            return Boolean(value) === Boolean(this.parent.guestPricePerKwh);
        }),
});
